import { useContext, useState, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "@tanstack/react-router";
// Context
import { AlertNotificationContext } from "../../contexts/AlertNotificationContext";
// State
import { useUserBranches } from "../../state/userBranchListState";
import { useStaffCardContractList } from "../../state/staffCardContractListState";
import { useStaffCardWardrobeList } from "../../state/staffCardWardrobeListState";
import { useMain } from "../../state/userMainState";
import { useSaveOrder } from "../../state/draftOrderSaveState";
// Hooks
import { useStaffUpdate } from "../../hooks/useStaffUpdate";
import { useCheckAPI } from "../../hooks/useCheckAPI";
// Components
import { RHFSelectField } from "../../components/RHFSelectField";
// MUI
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";

export const BulkStaffSummary = ({ staffData, onContinue, order }) => {
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);

  const hasStaff = Boolean(staffData?.staff);
  const currentStaff = staffData?.staff ?? {};

  const defaultValues = useMemo(
    () => ({
      staffid: currentStaff.staffid || "",
      name: currentStaff.name || "",
      branch: currentStaff.branch || "",
      gender: currentStaff.gender || "",
      uniform: currentStaff.uniform || "",
      yourid: currentStaff.yourid || "",
      contract: currentStaff.contract || "",
      email: currentStaff.email || "",
      blocked: currentStaff.blocked || "",
    }),
    [currentStaff],
  );

  const { control, handleSubmit, reset, watch } = useForm({ defaultValues });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { data: dataBranches } = useUserBranches();
  const branchArray = [
    ...(dataBranches?.data.data.data.list.branch || []),
  ].sort((a, b) => (a.name > b.name ? 1 : -1));

  const { data: dataContracts } = useStaffCardContractList();
  const contractArray = [
    ...(dataContracts?.data.data.data.list.contract || []),
  ].sort((a, b) => (a.description > b.description ? 1 : -1));

  const { data: dataWardrobes } = useStaffCardWardrobeList();
  const wardrobeArray = [
    ...(dataWardrobes?.data.data.data.list.wardrobe || []),
  ].sort((a, b) => (a.description > b.description ? 1 : -1));

  const { data: mainDataResponse } = useMain();
  const bodyTypes = mainDataResponse?.data?.data?.data?.data?.bodyTypes || [];

  const {
    mutate: updateStaffSave,
    isSuccess: updateStaffSuccess,
    data: updateStaffData,
  } = useStaffUpdate();

  const {
    mutate: saveOrder,
    isSuccess: saveOrderSuccess,
    data: saveOrderData,
    isPending: saveOrderPending,
  } = useSaveOrder();

  const onSubmit = (formData) => {
    updateStaffSave({
      data: {
        filter: { f_staffId: formData.staffid },
        staff: formData,
      },
    });
  };

  const updateStaffSuccessFunc = () => {
    setAlertNotification({
      message: "Staff member updated",
      severity: "success",
    });
    setEditMode(false);
  };

  const updateStaffErrorFunc = () => {
    setAlertNotification({
      message: `${updateStaffData.data.statusText}`,
      severity: "warning",
    });
  };

  useCheckAPI(
    updateStaffSuccess,
    updateStaffData,
    updateStaffData?.data?.status,
    updateStaffSuccessFunc,
    updateStaffErrorFunc,
  );

  const createOrderSuccessFunc = () => {
    const newOrderNo = saveOrderData?.data?.data?.data?.data?.order?.no || "";
    if (!newOrderNo) {
      setAlertNotification({
        message: "Order created but number missing",
        severity: "warning",
      });
      return;
    }
    navigate({ to: `/draft-bulk/${newOrderNo}` });
    onContinue?.();
  };

  const createOrderErrorFunc = () => {
    setAlertNotification({
      message: `Error: ${saveOrderData?.data?.status}: ${saveOrderData?.data?.statusText}`,
      severity: "error",
    });
  };

  useCheckAPI(
    saveOrderSuccess,
    saveOrderData,
    saveOrderData?.data?.status,
    createOrderSuccessFunc,
    createOrderErrorFunc,
  );

  const validationHelper = {
    branch: { required: "Branch is required" },
    contract: { required: "Contract is required" },
    uniform: { required: "Wardrobe is required" },
    gender: { required: "Item Types is required" },
  };

  const formValues = watch();
  const isBlocked = currentStaff.blocked === "Yes";

  const buildOrderPayload = () => {
    if (!order?.data) return null;
    const staffId = currentStaff.staffid;
    if (!staffId) return null;
    // const contractCode =
    //   currentStaff.contractcode || currentStaff.contract || "";
    const staffLineNo = order.data.staff?.[0]?.staffLineNo || 0;
    return {
      data: {
        filter: order.data.filter,
        order: {
          ...order.data.order,
          //   poNo: order.data.order.poNo || contractCode,
        },
        staff: [
          {
            no: staffId,
            staffLineNo,
            // staffpoNo: contractCode,
            reasonCode: "BULK",
          },
        ],
      },
    };
  };

  const handleCreateOrder = () => {
    const payload = buildOrderPayload();
    if (!payload) {
      setAlertNotification({
        message: "Unable to start order. Missing staff or order data.",
        severity: "warning",
      });
      return;
    }
    saveOrder(payload);
  };

  if (!hasStaff) {
    return (
      <Card sx={{ minWidth: 200 }}>
        <CardContent>
          <Typography variant="body2">
            Staff information not available yet. Please wait.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ minWidth: 200 }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="subtitle1">
              Create a Bulk Order allocated to: {formValues.name} (
              {formValues.yourid})
            </Typography>
            <Typography variant="body2">
              Details can be edited before continuing.
            </Typography>
            <RHFSelectField
              name="contract"
              label="Entity (Contract)"
              options={contractArray}
              optionIdentifiers={{ id: "no", label: "description" }}
              control={control}
              validationHelper={validationHelper}
              rules={{ required: true }}
              viewMode={!editMode}
            />
            <RHFSelectField
              name="branch"
              label="Site (Branch)"
              options={branchArray}
              optionIdentifiers={{ id: "no", label: "name" }}
              control={control}
              validationHelper={validationHelper}
              rules={{ required: true }}
              viewMode={!editMode}
            />
            <RHFSelectField
              name="uniform"
              label="Wardrobe"
              options={wardrobeArray}
              optionIdentifiers={{ id: "id", label: "description" }}
              control={control}
              validationHelper={validationHelper}
              rules={{ required: true }}
              viewMode={!editMode}
            />
            <RHFSelectField
              name="gender"
              label="Item Types"
              options={bodyTypes}
              optionIdentifiers={{ id: "code", label: "description" }}
              control={control}
              validationHelper={validationHelper}
              rules={{ required: true }}
              viewMode={!editMode}
            />
          </Stack>
        </CardContent>
        <CardActions sx={{ justifyContent: "space-between", px: 2, pb: 2 }}>
          <Stack direction="row" spacing={1}>
            {!editMode && (
              <Button
                type="button"
                variant="outlined"
                onClick={() => setEditMode(true)}
              >
                Edit details
              </Button>
            )}
            {editMode && (
              <>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => {
                    reset(defaultValues);
                    setEditMode(false);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" variant="contained" color="primary">
                  Save changes
                </Button>
              </>
            )}
          </Stack>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateOrder}
            disabled={
              !currentStaff?.staffid ||
              saveOrderPending ||
              isBlocked ||
              editMode
            }
          >
            {saveOrderPending ? "Loading.." : "Continue"}
          </Button>
        </CardActions>
        {isBlocked && (
          <Typography sx={{ px: 2, pb: 2, color: "error.main" }}>
            You are blocked from placing bulk orders. Please contact support.
          </Typography>
        )}
      </form>
    </Card>
  );
};
